/**
 * Backend error-body normalization, shared by the plain-fetch service clients.
 *
 * `AllExceptionsFilter` re-wraps a thrown `HttpException`, so an error body is
 * NOT `{ message: '...' }`. It is:
 *
 *   { success: false, statusCode: 404, path: '...',
 *     message: { message: 'Link not found', error: 'Not Found', statusCode: 404 } }
 *
 * and for a `ValidationPipe` rejection the nested `message` is an ARRAY of field
 * strings. So `body.message` is an object or an array far more often than it is a
 * string, and `message: body?.message || fallback` puts a non-string on the error
 * envelope. Any component that then renders `{error.message}` throws "Objects are
 * not valid as a React child" and takes its whole route down through
 * RouteErrorBoundary.
 *
 * These helpers unwrap one level, join a field-error array, then fall back, so the
 * `message` on an envelope is always a string. Confirmed against live responses
 * from `GET /public/share/:token/role` on 2026-07-29.
 */

const isPlainObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

const joinFieldErrors = (arr) => {
  // A ValidationPipe rejection arrives as an array of field messages. The form
  // should have caught these before the request, so seeing one means the form and
  // the DTO have drifted -- say which field, rather than a generic apology that
  // hides the drift.
  const strings = arr.filter((m) => typeof m === 'string' && m);
  return strings.length ? strings.join('. ') : null;
};

/**
 * Pull a displayable string out of an error body. Never returns a non-string.
 */
export const messageOf = (body, fallback) => {
  const raw = body?.message;
  if (typeof raw === 'string' && raw) return raw;
  if (isPlainObject(raw)) {
    if (typeof raw.message === 'string' && raw.message) return raw.message;
    if (Array.isArray(raw.message)) {
      const joined = joinFieldErrors(raw.message);
      if (joined) return joined;
    }
  }
  if (Array.isArray(raw)) {
    const joined = joinFieldErrors(raw);
    if (joined) return joined;
  }
  if (typeof body?.error === 'string' && body.error) return body.error;
  return fallback;
};

/**
 * The machine-readable code consumers branch on (`register_required`,
 * `slot_unavailable`, `already_scheduled`, `awaiting_provider_acceptance`,
 * `register_required_anon_used`). It arrives nested once the exception filter has
 * re-wrapped the throw, so check both levels.
 */
export const codeOf = (body) => body?.code || body?.message?.code || null;

/**
 * The remaining fields of the wrapped exception body, so callers can read them as
 * `error.<field>`. `AcceptSlotModal` depends on this for `claimListingUrl`: without
 * it the `register_required_anon_used` redirect silently stops firing.
 *
 * Array bodies are excluded on purpose -- spreading an array's rest yields numeric
 * keys (`{'0': '...', '1': '...'}`) and pollutes the envelope.
 */
export const extrasOf = (body) => {
  const raw = body?.message;
  if (!isPlainObject(raw)) return {};
  // eslint-disable-next-line no-unused-vars
  const { code: _code, message: _message, ...rest } = raw;
  return rest;
};

/**
 * Build the `{ status, code, message, ...extras }` error envelope.
 *
 * Extras are spread FIRST so a stray `status`/`code`/`message` field in the
 * backend body can never overwrite the real ones.
 */
export const toErrorEnvelope = (status, body, fallback) => ({
  ...extrasOf(body),
  status,
  code: codeOf(body),
  message: messageOf(body, fallback),
});

const AXIOS_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Axios-shaped adapter: the error body is at `err.response.data`. Falls back to
 * the axios error's own `message` (network/timeout text) before the caller's copy.
 */
export const extractErrorMessage = (err, fallback) => {
  const safeFallback = fallback || AXIOS_FALLBACK;
  if (!err) return safeFallback;
  const data = err?.response?.data;
  if (data) {
    const fromBody = messageOf(data, null);
    if (fromBody) return fromBody;
  }
  if (typeof err.message === 'string' && err.message) return err.message;
  return safeFallback;
};

/**
 * Axios-shaped adapter for the machine-readable code.
 */
export const extractErrorCode = (err) => codeOf(err?.response?.data);
